import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { Mountain, Map, BadgeCheck, Package, UserCheck, MapPin, Clock, CloudRain, Shirt } from "lucide-react";
import { Link } from "react-router-dom";
import { business } from "../data/business";
import { AddressLine } from "./AddressLine";

export function About() {
  const root = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const reduce =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (reduce) return;

      gsap.from(".about-img", {
        x: -40,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 75%" },
      });
      gsap.from(".about-col > *", {
        y: 24,
        opacity: 0,
        duration: 0.8,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 70%" },
      });
    },
    { scope: root },
  );

  return (
    <section ref={root} className="split">
      <div className="about-img about-video">
        {/*
         * Previously a YouTube embed (video 9Ax0mVC0M-A). YouTube started
         * gating embedded players behind a "Sign in to confirm you're not
         * a bot" challenge for traffic it can't fingerprint (Vercel edge
         * regions trip this frequently), which left visitors staring at a
         * dead frame on the home page. Swapped to the same `rafting-hero.mp4`
         * the cinematic hero plays — already cached after the first paint,
         * no external dependency, no anti-bot interstitial.
         */}
        <video
          src="/videos/rafting-hero.mp4?v=9"
          poster="/images/generated-1777507384714.png"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-label="Rafting on the Middle Ocoee"
        />
      </div>
      <div className="col about-col">
        <div className="eye-row">
          <Mountain aria-hidden="true" />
          <span className="label">A RAFTING EXPERIENCE LIKE NO OTHER</span>
        </div>
        <h2 className="h2">
          Feel the Spray of<br />Class IV Rapids
        </h2>
        <p className="body">
          Your guide leads you through the same five miles of Middle Ocoee
          rapids that hosted the 1996 Olympic whitewater events. The Cherokee
          National Forest closes around you. The river opens up beneath your raft.
        </p>
        {/* Middle vs Upper explainer — facts stay general so we don't
            mis-state operations specifics. Class ratings here are the public
            characterisation widely used for the Ocoee. */}
        <div className="about-explainer">
          <div>
            <strong>Middle Ocoee</strong>
            <span>Class III–IV · 5-mile run through the Cherokee National Forest. The classic Ocoee experience.</span>
          </div>
          <div>
            <strong>Upper Olympic Course</strong>
            <span>The 1996 Olympic whitewater course, sitting upstream of the Middle. Tougher water, longer day.</span>
          </div>
        </div>

        <ul className="feat-bullets">
          <li><Map aria-hidden="true" /> 5-mile Middle Ocoee run</li>
          <li><Package aria-hidden="true" /> Helmet, life jacket, paddle &amp; wetsuit (when cold) — all included</li>
          <li><Shirt aria-hidden="true" /> Closed-toe shoes &amp; a dry change of clothes</li>
          <li><CloudRain aria-hidden="true" /> Trips run on scheduled dam-release days; in severe weather or unsafe conditions we may reschedule for your safety</li>
          {/* The exact trip duration, guide credentials and minimum age are
              operations details we do not have on the original site. We surface
              the honest fallback rather than assert specific numbers. */}
          <li><Clock aria-hidden="true" /> Trip length & arrival time — we'll confirm at booking</li>
          <li><BadgeCheck aria-hidden="true" /> Guided trips with trained crew</li>
          <li><UserCheck aria-hidden="true" /> Minimum age varies by trip — call to confirm</li>
        </ul>

        <div className="about-meta">
          <Link to="/rafting" className="btn btn-gold btn-sm">View Trips & Pricing</Link>
          <span className="muted" style={{ fontFamily: "var(--font-ui)", fontSize: 13 }}>
            or call <a href={`tel:${business.phoneRaw}`}>{business.phone}</a>
          </span>
        </div>

        <div className="eye-row" style={{ marginTop: 6 }}>
          <MapPin aria-hidden="true" />
          <em className="muted about-address" style={{ fontFamily: "var(--font-ui)", fontSize: 13 }}>
            <span className="about-address-street"><AddressLine /></span>
            <span className="about-address-sep"> &mdash; </span>
            <span className="about-address-drive">~1 hr from Chattanooga, ~2.5 hrs from Atlanta</span>
          </em>
        </div>
      </div>
    </section>
  );
}
